import { X, Check, XCircle, Download, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getUrgencyBadge } from "./ReorderSuggestions";

const CreatePODialog = ({ open, onOpenChange, poItems, supplierName, poNotes, user, onSupplierChange, onNotesChange, onUpdateItem, onRemoveItem, onSubmit }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase">Create Purchase Order</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="supplier">Supplier Name (Optional)</Label>
              <Input id="supplier" value={supplierName} onChange={(e) => onSupplierChange(e.target.value)} placeholder="Enter supplier name" data-testid="po-supplier-input" />
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea id="notes" value={poNotes} onChange={(e) => onNotesChange(e.target.value)} placeholder="Additional notes for this PO" rows={2} data-testid="po-notes-input" />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Items (Sorted by Urgency)</Label>
            <div className="border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Part #</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Name</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Brand</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-slate-600 uppercase">Qty</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-slate-600 uppercase">Urgency</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-slate-600 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {poItems.map((item, index) => (
                    <tr key={item.part_number || `po-item-${index}`} className="hover:bg-slate-50" data-testid={`po-item-row-${index}`}>
                      <td className="px-3 py-2 font-medium">{item.part_number}</td>
                      <td className="px-3 py-2">{item.name}</td>
                      <td className="px-3 py-2">{item.brand || '-'}</td>
                      <td className="px-3 py-2">
                        <Input type="number" min="1" value={item.quantity} onChange={(e) => onUpdateItem(index, 'quantity', e.target.value)} className="w-20 text-center" data-testid={`po-item-qty-${index}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Select value={item.urgency} onValueChange={(value) => onUpdateItem(index, 'urgency', value)}>
                          <SelectTrigger className="w-28" data-testid={`po-item-urgency-${index}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="ghost" onClick={() => onRemoveItem(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`po-item-remove-${index}`}>
                          <X size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {poItems.length === 0 && <p className="text-center text-slate-500 py-4">No items in this PO</p>}
          </div>

          {user?.role !== 'admin' && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-sm text-blue-800"><strong>Note:</strong> This Purchase Order will be submitted for Admin approval before it can be exported.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-po-submit-btn">
            {user?.role === 'admin' ? 'Create & Approve PO' : 'Submit for Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ViewPODialog = ({ open, onOpenChange, selectedPO, user, editing, editItems, editSupplierName, editNotes, onSetEditing, onUpdateEditItem, onRemoveEditItem, onSupplierChange, onNotesChange, onUpdate, onApprove, onReject, onDelete, onExport, canDownload = true }) => {
  if (!selectedPO) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase flex items-center gap-3">
            {selectedPO.po_number}
            <span className={`text-xs px-2 py-1 rounded-sm uppercase ${
              selectedPO.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              selectedPO.status === 'approved' ? 'bg-green-100 text-green-800' :
              'bg-red-100 text-red-800'
            }`}>{selectedPO.status}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Created By</p>
              <p className="font-medium">{selectedPO.created_by_name}</p>
              <p className="text-xs text-slate-400 capitalize">{selectedPO.created_by_role}</p>
            </div>
            <div>
              <p className="text-slate-500">Created On</p>
              <p className="font-medium">{new Date(selectedPO.created_at).toLocaleDateString()}</p>
            </div>
            {selectedPO.approved_by_name && (
              <>
                <div>
                  <p className="text-slate-500">Approved By</p>
                  <p className="font-medium">{selectedPO.approved_by_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Approved On</p>
                  <p className="font-medium">{new Date(selectedPO.approved_at).toLocaleDateString()}</p>
                </div>
              </>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Supplier Name (Optional)</Label>
                  <Input value={editSupplierName} onChange={(e) => onSupplierChange(e.target.value)} data-testid="edit-po-supplier-input" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={editNotes} onChange={(e) => onNotesChange(e.target.value)} rows={2} data-testid="edit-po-notes-input" />
                </div>
              </div>
              <div className="border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase">Part #</th>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase">Name</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase">Qty</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase">Urgency</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {editItems.map((item, index) => (
                      <tr key={item.id || item.part_number || `edit-${index}`}>
                        <td className="px-3 py-2 font-medium">{item.part_number}</td>
                        <td className="px-3 py-2">{item.name}</td>
                        <td className="px-3 py-2">
                          <Input type="number" min="1" value={item.quantity} onChange={(e) => onUpdateEditItem(index, 'quantity', e.target.value)} className="w-20 text-center" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Select value={item.urgency || 'medium'} onValueChange={(value) => onUpdateEditItem(index, 'urgency', value)}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="critical">Critical</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Button size="sm" variant="ghost" onClick={() => onRemoveEditItem(index)} className="text-red-600 hover:text-red-700"><X size={16} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-slate-500 text-sm">Supplier</p>
                <p className="font-medium text-lg">{selectedPO.supplier_name}</p>
              </div>
              {selectedPO.notes && (
                <div>
                  <p className="text-slate-500 text-sm">Notes</p>
                  <p className="text-slate-700">{selectedPO.notes}</p>
                </div>
              )}
              <div className="border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase">#</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase">Part #</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase">Brand</th>
                      <th className="text-center px-4 py-3 text-xs font-bold uppercase">Qty</th>
                      <th className="text-center px-4 py-3 text-xs font-bold uppercase">Urgency</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedPO.items.map((item, index) => (
                      <tr key={item.id || item.part_number || `view-${index}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium">{item.part_number}</td>
                        <td className="px-4 py-3">{item.name}</td>
                        <td className="px-4 py-3">{item.brand || '-'}</td>
                        <td className="px-4 py-3 text-center font-bold">{item.quantity}</td>
                        <td className="px-4 py-3 text-center">{getUrgencyBadge(item.urgency || 'medium')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center pt-2">
                <p className="text-sm text-slate-500">
                  Total: <span className="font-bold text-slate-900">{selectedPO.items.reduce((sum, i) => sum + i.quantity, 0)} units</span>
                  {' '}({selectedPO.items.length} line items)
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {selectedPO.status === 'pending' && user?.role === 'admin' && (
            <>
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => onSetEditing(false)}>Cancel Edit</Button>
                  <Button onClick={onUpdate} className="bg-blue-600 hover:bg-blue-700 text-white">Save Changes</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => onSetEditing(true)} data-testid="edit-po-btn"><Edit2 size={16} className="mr-2" /> Edit</Button>
                  <Button variant="destructive" onClick={() => onDelete(selectedPO.id)} data-testid="delete-po-btn"><Trash2 size={16} className="mr-2" /> Delete</Button>
                  <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => onReject(selectedPO.id)}><XCircle size={16} className="mr-2" /> Reject</Button>
                  <Button onClick={() => onApprove(selectedPO.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-po-modal-btn"><Check size={16} className="mr-2" /> Approve</Button>
                </>
              )}
            </>
          )}
          {selectedPO.status === 'approved' && canDownload && (
            <>
              <Button variant="outline" onClick={() => onExport(selectedPO.id, 'csv')} data-testid="export-po-csv-btn"><Download size={16} className="mr-2" /> Export CSV</Button>
              <Button onClick={() => onExport(selectedPO.id, 'pdf')} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="export-po-pdf-btn"><Download size={16} className="mr-2" /> Export PDF</Button>
            </>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { CreatePODialog, ViewPODialog };
